import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, Ticket } from "lucide-react";
import { toast } from "sonner";
import { playSqueak } from "../lib/audio";
import { isAlly } from "../lib/theme";
import { getContent, saveContent } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_TEXTS = {
  vow: "I swear the oath: I will bring no cheese, speak of this to no cat, and respect every rat I meet in the pit.",
  submit: "Seal the oath",
};

export const RsvpForm = () => {
  const [alias, setAlias] = useState("");
  const [contact, setContact] = useState("");
  const [crew, setCrew] = useState(1);
  const [vow, setVow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pass, setPass] = useState(null);
  const [ally, setAllyState] = useState(isAlly);
  const [editing, setEditing] = useState(false);
  const [texts, setTexts] = useState(DEFAULT_TEXTS);

  useEffect(() => {
    const onTheme = (e) => setAllyState(e.detail.ally);
    window.addEventListener("rotu:theme", onTheme);
    return () => window.removeEventListener("rotu:theme", onTheme);
  }, []);

  useEffect(() => {
    const onEdit = (e) => setEditing(e.detail.editing);
    window.addEventListener("rotu:edit", onEdit);
    return () => window.removeEventListener("rotu:edit", onEdit);
  }, []);

  useEffect(() => {
    getContent("rsvp").then((v) => {
      if (v) setTexts({ vow: v.vow || DEFAULT_TEXTS.vow, submit: v.submit || DEFAULT_TEXTS.submit });
    });
  }, []);

  const saveField = (field, el) => {
    const value = el.innerText.trim();
    if (!value || value === texts[field]) return;
    const next = { ...texts, [field]: value };
    setTexts(next);
    saveContent("rsvp", next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!alias.trim()) {
      toast.error("The colony needs an alias.");
      return;
    }
    if (!vow) {
      toast.error("You must swear the oath. No cheese. No exceptions.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/rsvp`, {
        alias: alias.trim(),
        contact: contact.trim() || null,
        crew_size: crew,
        vow,
      });
      setPass(res.data);
      try {
        localStorage.setItem("rotu_rsvpd", res.data.entry_code);
      } catch (e) {}
      window.dispatchEvent(new CustomEvent("rotu:rsvpd"));
      const hunter = localStorage.getItem("rotu_hunter");
      if (hunter) {
        axios.post(`${API}/hunter/name`, { hunter, alias: res.data.alias }).catch(() => {});
      }
      playSqueak();
      toast.success("Entry pass minted. Guard it with your life.");
    } catch (err) {
      toast.error("Transmission failed. The sewers are congested. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="rsvp" className="relative z-10 px-6 sm:px-12 lg:px-24 py-28 sm:py-36" data-testid="rsvp-section">
      <div className="max-w-2xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-[11px] tracking-[0.5em] text-[#FF0055] uppercase mb-4 text-center"
        >
          Final transmission
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-white text-center mb-16"
        >
          Request <span className="text-[#39FF14]">entry</span>
        </motion.h2>

        <AnimatePresence mode="wait">
          {pass ? (
            <motion.div
              key="pass"
              initial={{ opacity: 0, scale: 0.9, rotateX: 20 }}
              animate={{ opacity: 1, scale: 1, rotateX: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className={`border bg-[#08080C]/90 backdrop-blur-xl p-10 text-center pulse-glow ${ally ? "rainbow-pass" : "border-[#39FF14]"}`}
              data-testid="rsvp-entry-pass"
            >
              <Ticket size={28} className={`mx-auto mb-6 ${ally ? "text-[#FF71CE]" : "text-[#39FF14]"}`} />
              <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-2">
                Entry pass // admit {pass.crew_size}
              </div>
              <div className={`font-display text-4xl sm:text-5xl font-black glitchy mb-4 ${ally ? "rainbow-text" : "text-[#39FF14]"}`} data-testid="entry-code">
                {pass.entry_code}
              </div>
              <div className="font-bebas text-2xl text-white tracking-wide mb-2">{pass.alias}</div>
              <p className="text-xs text-zinc-500 leading-relaxed">
                Screenshot this code. Burn your phone afterwards.
                <br />
                The gate recognizes the code, not your face.
              </p>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              onSubmit={submit}
              className="border border-white/10 bg-[#111118]/70 backdrop-blur-xl p-8 sm:p-12 space-y-8"
              data-testid="rsvp-form"
            >
              <div>
                <label className="block text-[10px] tracking-[0.35em] uppercase text-zinc-500 mb-3">
                  Name
                </label>
                <input
                  value={alias}
                  onChange={(e) => setAlias(e.target.value)}
                  maxLength={40}
                  placeholder="John Smith"
                  data-testid="rsvp-alias-input"
                  className="w-full bg-[#08080C] border border-white/15 focus:border-[#39FF14] outline-none px-4 py-3 text-sm text-white placeholder:text-zinc-700 transition-colors duration-300"
                />
              </div>

              <div>
                <label className="block text-[10px] tracking-[0.35em] uppercase text-zinc-500 mb-3">
                  Rat-Digits (Phone Number)
                </label>
                <input
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  maxLength={120}
                  placeholder="Gimme yo ratty digits"
                  data-testid="rsvp-contact-input"
                  className="w-full bg-[#08080C] border border-white/15 focus:border-[#00F0FF] outline-none px-4 py-3 text-sm text-white placeholder:text-zinc-700 transition-colors duration-300"
                />
              </div>

              <div>
                <label className="block text-[10px] tracking-[0.35em] uppercase text-zinc-500 mb-3">
                  Crew size
                </label>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => setCrew((c) => Math.max(1, c - 1))}
                    data-testid="rsvp-crew-minus"
                    className="w-10 h-10 border border-white/15 text-white hover:border-[#FF0055] hover:text-[#FF0055] transition-colors duration-200 flex items-center justify-center"
                  >
                    <Minus size={14} />
                  </button>
                  <span className="font-display text-2xl text-white tabular-nums w-10 text-center" data-testid="rsvp-crew-value">
                    {crew}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCrew((c) => Math.min(2, c + 1))}
                    data-testid="rsvp-crew-plus"
                    className="w-10 h-10 border border-white/15 text-white hover:border-[#39FF14] hover:text-[#39FF14] transition-colors duration-200 flex items-center justify-center"
                  >
                    <Plus size={14} />
                  </button>
                  <span className="text-[10px] tracking-[0.15em] uppercase text-zinc-600 leading-tight max-w-[180px]" data-testid="rsvp-crew-note">
                    (+1s must be confirmed with the council)
                  </span>
                </div>
              </div>

              <label
                className="flex items-start gap-3 cursor-pointer group"
                data-testid="rsvp-vow-checkbox"
                onClick={(e) => {
                  if (editing) e.preventDefault();
                }}
              >
                <input
                  type="checkbox"
                  checked={vow}
                  onChange={(e) => setVow(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#39FF14]"
                />
                <span
                  className={`text-xs text-zinc-400 leading-relaxed group-hover:text-zinc-200 transition-colors duration-200 ${editing ? "editable-box" : ""}`}
                  contentEditable={editing}
                  suppressContentEditableWarning
                  onBlur={(e) => saveField("vow", e.currentTarget)}
                  data-testid="rsvp-vow-text"
                >
                  {texts.vow}
                </span>
              </label>

              <button
                type={editing ? "button" : "submit"}
                disabled={loading}
                data-testid="rsvp-submit-button"
                className="group relative w-full py-4 border border-[#39FF14] text-[#39FF14] font-display text-sm font-bold uppercase tracking-widest overflow-hidden disabled:opacity-50"
              >
                <span className="absolute inset-0 bg-[#39FF14] translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                <span
                  className={`relative group-hover:text-[#08080C] transition-colors duration-300 ${editing ? "editable-box" : ""}`}
                  contentEditable={editing}
                  suppressContentEditableWarning
                  onBlur={(e) => saveField("submit", e.currentTarget)}
                  data-testid="rsvp-submit-text"
                >
                  {loading ? "Transmitting..." : texts.submit}
                </span>
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
